import { BaseModel } from '@cotera/client/v0/actions/framework';
import { isActiveRunStatus } from './workflow-run-status';

/**
 * The run fields the cancel/retry actions need. Both `WorkflowRun` (list rows)
 * and `WorkflowRunDetail` (detail view) satisfy this structurally.
 */
export type WorkflowRunActionTarget = {
  id: string;
  status: string;
};

/** Only in-progress runs can be cancelled; the server enforces this too. */
export const canCancelWorkflowRun = (run: WorkflowRunActionTarget): boolean =>
  isActiveRunStatus(run.status);

/**
 * Any finished run can be retried, including completed ones — a retry replays
 * that run's own definition snapshot with its original input as a new run.
 */
export const canRetryWorkflowRun = (run: WorkflowRunActionTarget): boolean =>
  !isActiveRunStatus(run.status);

/**
 * Carries which runs the cancel/retry actions should act on. Put in scope by
 * the runs list (synced from row focus / multi-select / the row action menu)
 * and by the run detail view (always the run being viewed), which is what lets
 * one pair of actions serve the list, the bulk selection bar, and the detail
 * header without each growing its own mutation code.
 */
export class WorkflowRunActionTargetsModel extends BaseModel {
  readonly workflowId: string;
  private _runs: WorkflowRunActionTarget[];

  constructor(params: {
    workflowId: string;
    runs?: WorkflowRunActionTarget[];
    /**
     * Distinguishes co-existing instances for the same workflow (the runs list
     * vs. a run detail view). Model ids are refcounted per scope, so two
     * instances sharing an id would collapse into the first one registered and
     * silently drop the second's targets.
     */
    scopeKey?: string;
  }) {
    super(
      `workflow-run-action-targets:${params.workflowId}${
        params.scopeKey !== undefined ? `:${params.scopeKey}` : ''
      }`
    );
    this.workflowId = params.workflowId;
    this._runs = params.runs ?? [];
  }

  setRuns(runs: WorkflowRunActionTarget[]): void {
    this._runs = runs;
  }

  get runs(): WorkflowRunActionTarget[] {
    return this._runs;
  }

  /**
   * A mixed selection narrows to the applicable subset rather than refusing —
   * selecting 3 running and 2 failed runs cancels the 3 and retries the 2.
   */
  get cancellableRuns(): WorkflowRunActionTarget[] {
    return this._runs.filter(canCancelWorkflowRun);
  }

  get retryableRuns(): WorkflowRunActionTarget[] {
    return this._runs.filter(canRetryWorkflowRun);
  }
}
